import type { AgentChatMessageApi } from '@/api/client';
import type { TrackerChatMessage } from '@/components/Messenger/types/chatMessageTypes';
import {
  collectPersistedAssistantIds,
} from '@/components/Messenger/agent/agentChatMessageMap';
import { mergeStableTrackerChatMessages } from '@/components/Messenger/agent/agentChatMessageStability';
import { mergeTrackerMessagesWithSocketPatchRows } from '@/components/Messenger/agent/agentChatMessageMerge';

export type AgentChatProcessingPhaseClient = 'submitted' | 'reasoning' | 'tool' | 'text' | null;

export type AgentChatServerSocketPatch = {
  chat_id: string;
  created_at?: string;
  preview?: string | null;
  schema_version?: number;
  messages?: AgentChatMessageApi[];
  last_read_message_id?: string | null;
  processing?: boolean;
  processing_phase?: AgentChatProcessingPhaseClient;
};

export type AgentChatStoreSnapshot = {
  messages: TrackerChatMessage[];
  persistedAssistantIds: Record<string, true>;
  lastReadUiMessageId: string | null;
  processing: boolean;
  processingPhase: AgentChatProcessingPhaseClient;
  loading: boolean;
  error: string | null;
  hydrated: boolean;
};

const EMPTY_SNAPSHOT: AgentChatStoreSnapshot = Object.freeze({
  messages: [],
  persistedAssistantIds: {},
  lastReadUiMessageId: null,
  processing: false,
  processingPhase: null,
  loading: false,
  error: null,
  hydrated: false,
});

const listenersByChatId = new Map<string, Set<() => void>>();
const entries = new Map<string, AgentChatStoreSnapshot>();

function emit(chatId: string) {
  const set = listenersByChatId.get(chatId);
  if (!set) return;
  for (const cb of set) cb();
}

export function ensureAgentChatSlot(chatId: string) {
  const id = chatId.trim();
  if (!id || entries.has(id)) return;
  entries.set(id, {
    messages: [],
    persistedAssistantIds: {},
    lastReadUiMessageId: null,
    processing: false,
    processingPhase: null,
    loading: false,
    error: null,
    hydrated: false,
  });
}

export function subscribeAgentChat(chatId: string | null, onChange: () => void): () => void {
  const id = chatId?.trim() || '';
  if (!id) return () => {};
  ensureAgentChatSlot(id);
  let set = listenersByChatId.get(id);
  if (!set) {
    set = new Set();
    listenersByChatId.set(id, set);
  }
  set.add(onChange);
  return () => {
    set!.delete(onChange);
    if (set!.size === 0) listenersByChatId.delete(id);
  };
}

export function getAgentChatSnapshot(chatId: string | null): AgentChatStoreSnapshot {
  const id = chatId?.trim() || '';
  if (!id) return EMPTY_SNAPSHOT;
  return entries.get(id) ?? EMPTY_SNAPSHOT;
}

export function agentChatReplaceAllFromApi(
  chatId: string,
  messages: TrackerChatMessage[],
  meta: {
    lastReadUiMessageId: string | null;
    processing: boolean;
    processingPhase: AgentChatProcessingPhaseClient;
    loading?: boolean;
    error?: string | null;
  }
) {
  const id = chatId.trim();
  if (!id) return;
  ensureAgentChatSlot(id);
  const prev = entries.get(id)!;
  const merged = mergeStableTrackerChatMessages(prev.messages, messages);
  const persisted = collectPersistedAssistantIds(merged);
  const next: AgentChatStoreSnapshot = {
    ...prev,
    messages: merged,
    persistedAssistantIds: persisted,
    lastReadUiMessageId: meta.lastReadUiMessageId,
    processing: meta.processing,
    processingPhase: meta.processingPhase,
    loading: meta.loading ?? false,
    error: meta.error ?? null,
    hydrated: true,
  };
  entries.set(id, next);
  emit(id);
}

export function agentChatApplyServerSocketPatch(
  chatId: string,
  prevUiMessages: TrackerChatMessage[],
  patch: AgentChatServerSocketPatch
): {
  messages: TrackerChatMessage[];
  persistedAssistantIds: Record<string, true>;
  lastReadUiMessageId: string | null;
  processing: boolean;
  processingPhase: AgentChatProcessingPhaseClient;
} | null {
  const id = chatId.trim();
  if (!id) return null;
  if (patch.schema_version !== 2 || !Array.isArray(patch.messages) || patch.messages.length === 0) {
    return null;
  }
  const merged = mergeTrackerMessagesWithSocketPatchRows(prevUiMessages, patch.messages);
  const persisted = collectPersistedAssistantIds(merged);
  ensureAgentChatSlot(id);
  const prevSnap = entries.get(id)!;
  const lastRead =
    patch.last_read_message_id !== undefined ? patch.last_read_message_id : prevSnap.lastReadUiMessageId;
  const processing = patch.processing !== undefined ? patch.processing : prevSnap.processing;
  const phase =
    patch.processing_phase !== undefined ? patch.processing_phase : prevSnap.processingPhase;
  entries.set(id, {
    ...prevSnap,
    messages: merged,
    persistedAssistantIds: persisted,
    lastReadUiMessageId: lastRead,
    processing,
    processingPhase: phase,
    hydrated: true,
  });
  emit(id);
  return { messages: merged, persistedAssistantIds: persisted, lastReadUiMessageId: lastRead, processing, processingPhase: phase };
}

export function agentChatReset(chatId: string) {
  const id = chatId.trim();
  if (!id) return;
  entries.delete(id);
  listenersByChatId.delete(id);
}

export function parseAgentChatServerSocketPatch(raw: unknown): AgentChatServerSocketPatch | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const chatIdRaw = o.chat_id;
  if (typeof chatIdRaw !== 'string' || !chatIdRaw.trim()) return null;
  const messagesRaw = o.messages;
  const messages = Array.isArray(messagesRaw)
    ? messagesRaw.filter((x): x is AgentChatMessageApi => {
        return Boolean(x && typeof x === 'object' && typeof (x as AgentChatMessageApi).ui_message_id === 'string');
      })
    : undefined;
  const lastRead =
    o.last_read_message_id === null || typeof o.last_read_message_id === 'string'
      ? (o.last_read_message_id as string | null)
      : undefined;
  const processing = typeof o.processing === 'boolean' ? o.processing : undefined;
  const processing_phase = o.processing_phase as AgentChatProcessingPhaseClient | undefined;
  const schema_version = typeof o.schema_version === 'number' ? o.schema_version : undefined;
  const preview = o.preview === null || typeof o.preview === 'string' ? (o.preview as string | null) : undefined;
  const created_at = typeof o.created_at === 'string' ? o.created_at : undefined;
  return {
    chat_id: chatIdRaw.trim(),
    created_at,
    preview,
    schema_version,
    messages,
    last_read_message_id: lastRead,
    processing,
    processing_phase,
  };
}
